/**
 * Guardie di plausibilità sull'energia fatturabile (RFC stato-colonnine §6.2).
 * `Energy.Active.Import.Register` è un registro → MONOTONO non decrescente per
 * sessione, e il delta non può superare potenza_max × Δt. Una violazione = dato
 * di una colonnina non conforme: NON si fattura in silenzio.
 */

export interface EnergyPlausibilityInput {
  /** Nuova lettura energia in Wh (già normalizzata dall'unità). */
  newEnergyWh: number;
  /** Ultima energia nota per la sessione in Wh (session.energy). undefined = prima lettura. */
  lastEnergyWh?: number | null;
  /** Millisecondi trascorsi dall'ultima lettura (per lo spike check). */
  elapsedMs?: number | null;
  /** Potenza massima plausibile del connettore in W (dal quirk profile o dal connettore). */
  maxPowerW?: number | null;
}

export type EnergyPlausibilityReason = 'non_finite' | 'negative' | 'non_monotonic' | 'spike';

export interface EnergyPlausibilityResult {
  plausible: boolean;
  reason?: EnergyPlausibilityReason;
  /** Dettaglio human-readable per audit/timeline. */
  detail?: string;
}

/** Margine sullo spike: consente picchi/arrotondamenti fino al 50% sopra il teorico. */
const SPIKE_MARGIN = 1.5;

export function evaluateEnergyPlausibility(input: EnergyPlausibilityInput): EnergyPlausibilityResult {
  const { newEnergyWh, lastEnergyWh, elapsedMs, maxPowerW } = input;

  if (!Number.isFinite(newEnergyWh)) return { plausible: false, reason: 'non_finite', detail: `energy=${newEnergyWh}` };
  if (newEnergyWh < 0) return { plausible: false, reason: 'negative', detail: `energy=${newEnergyWh}` };

  if (lastEnergyWh != null && Number.isFinite(lastEnergyWh)) {
    // Monotonicità: il registro non torna indietro (tolleranza minima per il rumore).
    if (newEnergyWh < lastEnergyWh - 1) {
      return { plausible: false, reason: 'non_monotonic', detail: `new=${newEnergyWh} < last=${lastEnergyWh}` };
    }
    // Spike: delta energia oltre potenza_max × Δt (× margine).
    if (elapsedMs && elapsedMs > 0 && maxPowerW && maxPowerW > 0) {
      const maxDeltaWh = (maxPowerW * elapsedMs) / 3_600_000; // W·ms → Wh
      const deltaWh = newEnergyWh - lastEnergyWh;
      if (deltaWh > maxDeltaWh * SPIKE_MARGIN) {
        return { plausible: false, reason: 'spike', detail: `delta=${deltaWh.toFixed(0)}Wh > max=${(maxDeltaWh * SPIKE_MARGIN).toFixed(0)}Wh` };
      }
    }
  }

  return { plausible: true };
}
